import { VoucherType } from '../constants/voucher.constants';
import type { Voucher, VoucherApplicationState, VoucherValidationResult } from '../types/voucher.types';

/**
 * Validate voucher code
 */
export const validateVoucherCode = (
    code: string,
    totalPrice: number,
    findVoucherFn: (code: string) => Voucher | undefined
): VoucherValidationResult => {
    // Check if code is empty
    if (!code || code.trim().length === 0) {
        return {
            isValid: false,
            error: 'Zadejte slevový kód',
        };
    }

    // Find voucher
    const voucher = findVoucherFn(code);

    if (!voucher) {
        return {
            isValid: false,
            error: 'Neplatný slevový kód',
        };
    }

    // Check if voucher is active
    if (!voucher.isActive) {
        return {
            isValid: false,
            error: 'Tento slevový kód již není aktivní',
        };
    }

    // Check expiration
    if (voucher.expiresAt && new Date(voucher.expiresAt) < new Date()) {
        return {
            isValid: false,
            error: 'Tento slevový kód již vypršel',
        };
    }

    // Check minimum purchase amount
    if (voucher.minPurchaseAmount && totalPrice < voucher.minPurchaseAmount) {
        return {
            isValid: false,
            error: `Minimální částka pro použití tohoto kódu je ${voucher.minPurchaseAmount} Kč`,
        };
    }

    return {
        isValid: true,
        voucher,
    };
};

/**
 * Calculate discount amount based on voucher type
 */
export const calculateVoucherDiscount = (voucher: Voucher, totalPrice: number): number => {
    if (voucher.type === VoucherType.PERCENTAGE) {
        // Calculate percentage discount
        return Math.round((totalPrice * voucher.value) / 100);
    } else if (voucher.type === VoucherType.FIXED_AMOUNT) {
        // Fixed amount discount (cannot exceed total price)
        return Math.min(voucher.value, totalPrice);
    }
    return 0;
};

/**
 * Apply voucher to total price and return applied voucher info
 */
export const applyVoucher = (voucher: Voucher, totalPrice: number): VoucherApplicationState => {
    const discountAmount = calculateVoucherDiscount(voucher, totalPrice);
    return {
        voucher,
        discountAmount,
    };
};

/**
 * Calculate final price after voucher discount
 */
export const calculateFinalPrice = (totalPrice: number, appliedVoucher?: VoucherApplicationState): number => {
    if (!appliedVoucher) {
        return totalPrice;
    }
    return Math.max(0, totalPrice - appliedVoucher.discountAmount);
};

/**
 * Build a case-insensitive voucher lookup function from a list of vouchers.
 */
export const findVoucherByCodeFromList = (vouchers: Voucher[]) => (code: string): Voucher | undefined => {
    return vouchers.find(
        (voucher) => voucher.code.toLowerCase() === code.trim().toLowerCase() && voucher.isActive
    );
};
